import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  InputElement,
  InputGroup,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react"
import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import PropertyCard, {
  getEpcBadge,
  getHomePropertyDetailLine,
  getStatusBadge,
} from "../components/portfolio/PropertyCard"
import { SelectFilter } from "../components/SelectFilter"
import EpcBandFilter, {
  EPC_FILTER_OPTIONS,
  parseBands,
  serialiseBands,
} from "../components/EpcBandFilter"
import StateBox from "../components/common/StateBox"
import ViewModeToggle, { type ViewMode } from "../components/common/ViewModeToggle"
import PropertyMapView, {
  toMapCoordinate,
  type PropertyMapItem,
} from "../components/maps/PropertyMapView"
import { useActivePortfolio } from "../hooks/useActivePortfolio"
import { useDebounce } from "../hooks/useDebounce"
import { useInfiniteScroll } from "../hooks/useInfiniteScroll"
import { useLocalStorage } from "../hooks/useLocalStorage"
import {
  useInfiniteHomes,
  usePortfolioStats
} from "../hooks/usePortfolioQueries"
import { exportHomesAnalysisCsv } from "../services/portfolioApi"
import { toaster } from "../toaster"

const isBatchImportEnabled =
  process.env.REACT_APP_ENABLE_BATCH_IMPORT === "true"

const getFilenameFromContentDisposition = (contentDisposition?: string) => {
  if (!contentDisposition) return null

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, ""))

  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
  return filenameMatch?.[1] ?? null
}

const buildExportFilename = (portfolioName?: string) => {
  const name = (portfolioName || "my-homes")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  return `${name || "my-homes"}-analysis.csv`
}

const MyHomes: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { activePortfolioId, activePortfolioName, isQuotaReached } = useActivePortfolio()

  const [builtFormFilter, setBuiltFormFilter] = useLocalStorage("myHomes.builtFormFilter", "")
  const [epcBandsRaw, setEpcBandsRaw] = useLocalStorage("myHomes.epcBands", "")
  const [sortOption, setSortOption] = useLocalStorage<
    "recent" | "alphabetical" | "oldest" | "highest_roi"
  >("myHomes.sortOption", "recent")
  const [tenantPowerMode, setTenantPowerMode] = useLocalStorage("myHomes.tenantPowerMode", false)
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>("myHomes.viewMode", "list")

  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearch = useDebounce(searchQuery, 800)
  const [isExporting, setIsExporting] = useState(false)
  const currentViewMode: ViewMode = viewMode === "map" ? "map" : "list"

  const roiPotentialFilter = searchParams.get("roiPotential") ?? ""

  const handleRoiPotentialFilterChange = (value: string) => {
    const nextSearchParams = new URLSearchParams(searchParams)
    if (value) {
      nextSearchParams.set("roiPotential", value)
    } else {
      nextSearchParams.delete("roiPotential")
    }
    setSearchParams(nextSearchParams, { replace: true })
  }

  const epcBands = useMemo(() => parseBands(epcBandsRaw), [epcBandsRaw])
  const setEpcBands = (bands: string[]) => setEpcBandsRaw(serialiseBands(bands))

  // Selecting everything filters nothing, so save the round trip of sending all eight.
  const epcBandsParam =
    epcBands.length === EPC_FILTER_OPTIONS.length ? undefined : epcBands

  // Reset filters when the active portfolio changes
  useEffect(() => {
    setBuiltFormFilter("")
    setEpcBandsRaw("")
    setSearchQuery("")
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePortfolioId])

  useEffect(() => {
    if (roiPotentialFilter) {
      setTenantPowerMode(false)
    }
  }, [roiPotentialFilter, setTenantPowerMode])

  const {
    data: homesPages,
    isLoading: homesLoading,
    error: homesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteHomes(
    20,
    activePortfolioId,
    debouncedSearch || undefined,
    builtFormFilter || undefined,
    sortOption,
    roiPotentialFilter || undefined,
    undefined,
    undefined,
    epcBandsParam
  )

  const homes = useMemo(
    () => homesPages?.pages.flatMap((page) => page.homes) ?? [],
    [homesPages]
  )

  const mapItems = useMemo<PropertyMapItem[]>(
    () =>
      homes.map((home) => {
        const epcBadge = getEpcBadge(home.features.currentEpcRating)
        const statusBadge = getStatusBadge(home.status)
        return {
          id: `home-${home.id}`,
          homeId: home.id,
          address: home.fullAddress,
          lat: toMapCoordinate(home.lat),
          lng: toMapCoordinate(home.lng),
          imageUrl: home.imageUrl ?? null,
          detailLine: getHomePropertyDetailLine(home),
          badges: [statusBadge, epcBadge].filter(Boolean) as PropertyMapItem["badges"],
          roi: tenantPowerMode ? home.tenantPowerRoi : home.upgradesRoi,
          roiLabel: tenantPowerMode ? "Tenant Power ROI" : "Green ROI",
          roiColor: tenantPowerMode ? "#5840FF" : undefined,
          upgradeCost: home.upgradesTotalCost,
          paybackPeriod: tenantPowerMode ? home.tenantPowerPaybackPeriod : home.upgradesPaybackPeriod,
          paybackColor: tenantPowerMode ? "#5840FF" : undefined,
          showMetrics: home.status === "analysed",
        }
      }),
    [homes, tenantPowerMode]
  )

  const loadMoreRef = useInfiniteScroll(fetchNextPage, hasNextPage, isFetchingNextPage)

  const { data: statsData } = usePortfolioStats(activePortfolioId)

  const epcCounts = useMemo(
    () => Object.fromEntries((statsData?.epcBandCounts ?? []).map((c) => [c.band, c.count])),
    [statsData]
  )

  const handleExportCsv = async () => {
    setIsExporting(true)
    try {
      const response = await exportHomesAnalysisCsv({
        portfolioId: activePortfolioId,
        search: debouncedSearch || undefined,
        builtForm: builtFormFilter || undefined,
        sort: sortOption,
        roiPotential: roiPotentialFilter || undefined
      })
      const blob = new Blob([response.data], { type: response.headers["content-type"] || "text/csv" })
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        buildExportFilename(activePortfolioName)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Error exporting homes analysis:", error)
      toaster.create({
        title: "Export failed",
        description: "There was an error exporting your homes. Please try again.",
        type: "error",
        duration: 4000,
      })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Box p={8}>
      <VStack gap={6} align="stretch">
        <HStack justify="space-between" align="center" w="full">
          <Heading size="xl">{activePortfolioName}</Heading>
          <HStack gap={2} align="center">
            <Button
              variant="outline"
              onClick={handleExportCsv}
              size="md"
              loading={isExporting}
              disabled={homesLoading || homes.length === 0}
            >
              Export data
            </Button>
            {isBatchImportEnabled && (
              <Button
                variant="outline"
                onClick={() =>
                  navigate("/bulk-import", {
                    state: { portfolioId: activePortfolioId }
                  })
                }
                size="md"
              >
                Import batch
              </Button>
            )}
            <Button
              bg={isQuotaReached ? "gray.400" : "green.800"}
              color="white"
              _hover={{ bg: isQuotaReached ? "gray.400" : "green.700" }}
              onClick={() =>
                !isQuotaReached &&
                navigate("/add-property", {
                  state: { portfolioId: activePortfolioId }
                })
              }
              size="md"
              disabled={isQuotaReached}
            >
              {isQuotaReached ? "Quota Reached" : "Add a home"}
            </Button>
          </HStack>
        </HStack>

        {homesLoading ? (
          <StateBox>
            <Spinner size="lg" />
            <Text mt={4}>Loading homes...</Text>
          </StateBox>
        ) : homesError ? (
          <StateBox>
            <Text color="red.500">Error loading homes. Please try again.</Text>
          </StateBox>
        ) : (
          <>
            {homes.length === 0 && !debouncedSearch && !builtFormFilter && !epcBandsParam && !roiPotentialFilter ? (
              <StateBox>
                <VStack gap={6}>
                  <Image
                    src="/images/placeholer-add-homes.png"
                    alt="Add homes"
                    width={240}
                    height={240}
                  />
                  <Text fontSize="lg" color="gray.500">
                    To get started, add all the addresses you'd like Greenflip
                    to analyse.
                  </Text>
                  <HStack gap={3} justify="center" flexWrap="wrap">
                    <Button
                      bg={isQuotaReached ? "gray.400" : "green.800"}
                      color="white"
                      size="lg"
                      px={8}
                      _hover={{
                        bg: isQuotaReached ? "gray.400" : "green.700"
                      }}
                      disabled={isQuotaReached}
                      onClick={() =>
                        !isQuotaReached &&
                        navigate("/add-property", {
                          state: { portfolioId: activePortfolioId }
                        })
                      }
                    >
                      {isQuotaReached ? "Quota Reached" : "Add a home"}
                    </Button>
                    {isBatchImportEnabled && (
                      <Button
                        variant="outline"
                        size="lg"
                        px={8}
                        onClick={() =>
                          navigate("/bulk-import", {
                            state: { portfolioId: activePortfolioId }
                          })
                        }
                      >
                        Import batch
                      </Button>
                    )}
                  </HStack>
                </VStack>
              </StateBox>
            ) : (
              <>
                <HStack
                  justify="space-between"
                  align={{ base: "stretch", md: "flex-end" }}
                  flexWrap="wrap"
                  gap={4}
                >
                  <VStack align="start" gap={2} minW="200px">
                    <Text fontWeight="semibold">Search / Filter by</Text>
                    <HStack gap={3} align="center" flexWrap="wrap">
                      <InputGroup
                        size="sm"
                        maxW="320px"
                        startElement={
                          <InputElement pointerEvents="none">
                            <Icon color="gray.400" boxSize={4}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                              </svg>
                            </Icon>
                          </InputElement>
                        }
                      >
                        <Input
                          placeholder="Search address..."
                          value={searchQuery}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setSearchQuery(e.target.value)
                          }
                          borderRadius="8px"
                        />
                      </InputGroup>
                      <SelectFilter value={builtFormFilter} onChange={setBuiltFormFilter} placeholder>
                        <option value="">All built forms</option>
                        {(statsData?.availableBuiltForms ?? []).map((form) => (
                          <option key={form} value={form}>
                            {form}
                          </option>
                        ))}
                      </SelectFilter>
                      <SelectFilter
                        value={roiPotentialFilter}
                        onChange={handleRoiPotentialFilterChange}
                        placeholder
                      >
                        <option value="">ROI: Any</option>
                        <option value="high">ROI: 8%+</option>
                        <option value="medium">ROI: 4–8%</option>
                        <option value="low">ROI: &lt;4%</option>
                      </SelectFilter>
                      <EpcBandFilter
                        value={epcBands}
                        onChange={setEpcBands}
                        counts={epcCounts}
                      />
                    </HStack>
                  </VStack>
                  <HStack gap={3} align="center" ml="auto">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
                      onClick={() => setTenantPowerMode(!tenantPowerMode)}
                    >
                      <div style={{
                        width: "44px",
                        height: "24px",
                        borderRadius: "12px",
                        background: tenantPowerMode ? "#276749" : "#CBD5E0",
                        position: "relative",
                        transition: "background 0.2s",
                        flexShrink: 0,
                      }}>
                        <div style={{
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          background: "white",
                          position: "absolute",
                          top: "3px",
                          left: tenantPowerMode ? "23px" : "3px",
                          transition: "left 0.2s",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                        }} />
                      </div>
                      <Text fontSize="sm" fontWeight="medium" color={tenantPowerMode ? "green.800" : "gray.500"}>
                        Tenant Power ROI
                      </Text>
                    </label>
                    <Box w="1px" h="24px" bg="gray.200" />
                    {currentViewMode === "list" && (
                      <>
                        <Text fontSize="sm" color="gray.500" fontWeight="medium">Sort by</Text>
                        <SelectFilter
                          value={sortOption}
                          onChange={(v) => setSortOption(v as typeof sortOption)}
                        >
                          <option value="alphabetical">Alphabetical</option>
                          <option value="recent">Most recent</option>
                          <option value="oldest">Oldest first</option>
                          <option value="highest_roi">Highest ROI</option>
                        </SelectFilter>
                        <Box w="1px" h="24px" bg="gray.200" />
                      </>
                    )}
                    <ViewModeToggle value={currentViewMode} onChange={setViewMode} />
                  </HStack>
                </HStack>
                {homes.length === 0 ? (
                  <StateBox>
                    <Text color="gray.600">
                      No homes match the current filters.
                    </Text>
                  </StateBox>
                ) : currentViewMode === "map" ? (
                  <PropertyMapView
                    items={mapItems}
                    storageKey={`myHomes.mapViewport.${activePortfolioId ?? "default"}`}
                    onViewDetails={(item) => navigate(`/home/${item.homeId}`)}
                    hasMore={hasNextPage}
                    isLoadingMore={isFetchingNextPage}
                    onLoadMore={() => fetchNextPage()}
                  />
                ) : (
                  <VStack gap={4} align="stretch">
                    {homes.map((home) => (
                      <PropertyCard
                        key={home.id}
                        home={home}
                        onClick={(id) => navigate(`/home/${id}`)}
                        showTenantPowerRoi={tenantPowerMode}
                      />
                    ))}
                    {hasNextPage && (
                      <Box ref={loadMoreRef} py={4} textAlign="center">
                        <HStack justify="center" gap={2}>
                          <Spinner size="sm" color="gray.400" />
                          <Text color="gray.500">Loading more...</Text>
                        </HStack>
                      </Box>
                    )}
                  </VStack>
                )}
              </>
            )}
          </>
        )}
      </VStack>
    </Box>
  )
}

export default MyHomes
